import {
  Component,
  inject,
  input,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { getUserData } from '../../../api/auth';
import { Permission, UserReference } from '../../../api/types';
import {
  SynButtonComponent,
  SynIconComponent,
  SynIconButtonComponent,
  SynSelectComponent,
  SynOptionComponent,
  SynDialogComponent,
  SynTooltipComponent,
} from '@synergy-design-system/angular';
import { ColumnDescription, TableComponent } from '../../components/table';
import { LocalManagementRoleMember } from '../../types';
import { UserSearchComponent } from '../../components/user-search.component';

@Component({
  selector: 'app-client-permissions',
  template: `
    <div class="flex-col margin-bottom-1">
      <span i18n="@@client-permissions-description">
        Grant permissions to access this client's configuration. These can be
        used to enable different functionality for different users in the
        Service Portal.
      </span>
      <span i18n="@@client-permissions-vs-roles" class="margin-bottom-1">
        Note: To differenciate users in your application, you should use the
        roles managed in the roles tab.
      </span>
      <span i18n="@@available-permissions">Available Permissions:</span>
      <ul>
        <li>
          <strong i18n="@@client-admin">Client Admin</strong>:
          <span i18n="@@client-admin-description">Change all possible configurations of the client.</span>
        </li>
        <li>
          <strong i18n="@@role-admin">Role Admin</strong>:
          <span i18n="@@role-admin-description">Can create new roles and assign users to roles.</span>
        </li>
        <li>
          <strong i18n="@@role-manager">Role Manager</strong>:
          <span i18n="@@role-manager-description">Can only assign users to existing roles.</span>
        </li>
      </ul>
      <span i18n="@@client-permissions-access-for-external-users">
        Access to the serviceportal is restricted to SICK employees by default.
        If you want to grant access to external users, contact us via sickid-id-service&#64;sick.com
      </span>
    </div>

    <div class="flex-row gap-1 margin-bottom-1" style="padding-left: 5px;">
      <syn-select
        [(value)]="userRole"
        placeholder="Choose Role"
        i18n-placeholder="@@choose-admin-role"
        [maxOptionsVisible]="3"
        size="medium"
        required
      >
        <syn-option value="client_admin" i18n="@@client-admin">Client Admin</syn-option>
        <syn-option value="role_admin" i18n="@@role-admin">Role Admin</syn-option>
        <syn-option value="role_manager" i18n="@@role-manager">Role Manager</syn-option>
      </syn-select>

      <app-user-search
        actionText="Add User"
        i18n-actionText="@@add-user"
        (userSelected)="onAddUser($event)"
        class="flex-grow-1"
      />
    </div>
    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let user = identity(value);
      <tr>
        <td>{{ user.username }}</td>
        <td>
          {{ user.email }}
          @if (!user.hasServiceportalAccess) {
            <syn-tooltip style="" i18n-content="@@missing-serviceportal-access"
                         content="User doesn't have access to the service portal.">
              <syn-icon style="color: var(--syn-color-warning-500); font-size: var(--syn-font-size-large);"
                        name="info_fill"
                        size="small" />
            </syn-tooltip>
          }
        </td>
        <td>{{ user.firstName }}</td>
        <td>{{ user.lastName }}</td>
        <td>
          <div class="flex-row">
            <syn-select
              [maxOptionsVisible]="3"
              size="small"
              [(value)]="user.mgmtRole"
              [disabled]="user.username === authUsername"
              helpText=""
            >
              <syn-option value="client_admin" i18n="@@client-admin">Client Admin</syn-option>
              <syn-option value="role_admin" i18n="@@role-admin">Role Admin</syn-option>
              <syn-option value="role_manager" i18n="@@role-manager">Role Manager</syn-option>
            </syn-select>
          </div>
        </td>
        <td>
          <syn-icon-button
            name="save_fill"
            label="Save Permission"
            i18n-label="@@save-permission"
            size="medium"
            [disabled]="user.oldMgmtRole === user.mgmtRole"
            (click)="onUpdatePermission(user)"
          />
          <syn-icon-button
            name="delete_fill"
            label="Remove Permission"
            i18n-label="@@remove-permission"
            size="medium"
            [disabled]="user.username === authUsername"
            (click)="openDeleteDialog(user)"
          />
        </td>
      </tr>
    </ng-template>
    <app-table
      [columns]="columns"
      [values]="members()"
      [rowTemplate]="rowTemplate"
      data-e2e="permissions-table"
    />

    <syn-dialog
      label="Delete Permission"
      i18n-label="@@delete-permission"
      #deleteDialog
    >
      <div>
        <p i18n="@@delete-permission-warning">
          Do you want to <strong>revoke</strong> the permissions of
          {{ this.memberToDelete?.email }}?
        </p>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button (click)="closeDeletePermissionDialog()"
        >
          <span i18n="@@cancel">Cancel</span>
        </syn-button
        >
        <syn-button
          variant="filled"
          i18n="@@delete-member"
          (click)="onRemoveMember(memberToDelete)"
        >Delete Member
        </syn-button
        >
      </div>
    </syn-dialog>
  `,
  styles: [``],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynIconButtonComponent,
    SynSelectComponent,
    SynOptionComponent,
    TableComponent,
    SynDialogComponent,
    SynTooltipComponent,
    UserSearchComponent,
  ],
})
export class ClientPermissionsComponent {
  id = inject(ActivatedRoute).snapshot.params['id'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  adminRoles = input.required<Permission[]>();
  members = model.required<LocalManagementRoleMember[]>();

  removingMember = signal(false);

  userRole = signal('client_admin');

  // auth username to disable change of own client
  authUsername = getUserData().username;

  memberToDelete?: LocalManagementRoleMember;

  @ViewChild(SynDialogComponent) deleteDialog!: SynDialogComponent;
  @ViewChild(UserSearchComponent) addMemberInput!: UserSearchComponent;

  columns: ColumnDescription<LocalManagementRoleMember>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (member) => member.username,
    },
    {
      label: $localize`:@@email:Email`,
      getField: (member) => member.email ?? '',
    },
    {
      label: $localize`:@@first-name:First Name`,
      getField: (member) => member.firstName ?? '',
    },
    {
      label: $localize`:@@last-name:Last Name`,
      getField: (member) => member.lastName ?? '',
    },
    {
      label: $localize`:@@permissions:Permissions`,
      getField: (member) => member.mgmtRole ?? '',
    },
    {
      label: $localize`:@@remove:Remove`,
      getField: () => '',
    },
  ];

  addUserToRole(user: UserReference, role: string) {
    this.api.createAdminPermissionRoleMember(
      this.id,
      { id: user.id, username: user.username, permissionRole: role },
      (newMember) => {
        this.addMemberInput.clear();

        const alreadyMember = this.members().some(
          (member) => member.id === newMember.id,
        );
        if (alreadyMember) return;
        // set role in "local" mgmtMember
        const newMgmtMember = newMember as LocalManagementRoleMember;
        newMgmtMember.mgmtRole = role;
        newMgmtMember.oldMgmtRole = role;

        const newMembers = [newMgmtMember, ...this.members()];
        this.members.set(newMembers);
      },
      (statusCode: number) => {
        if (statusCode === 409) {
          this.addMemberInput.setError(
            $localize`:@@error-user-has-permission:The user already has a permission for this client.`,
          );
        } else if (statusCode === 404) {
          this.addMemberInput.setUserDoesNotExistError();
        } else {
          this.addMemberInput.setError(
            $localize`:@@error-bad-request:Bad request.`,
          );
        }
      },
    );
  }

  onAddUser(userToAdd: UserReference) {
    const role = this.userRole();
    this.addUserToRole(userToAdd, role);
  }

  onUpdatePermission(memberToUpdate: LocalManagementRoleMember) {
    const username = memberToUpdate.username;
    const permissionRole = memberToUpdate.mgmtRole;
    this.api.updateAdminPermissionRole(
      this.id,
      memberToUpdate.id,
      { username, permissionRole },
      () => {
        this.members.update((members) => {
          const updatedMembers = members.filter(
            (member) => member !== memberToUpdate,
          );
          memberToUpdate.oldMgmtRole = memberToUpdate.mgmtRole;
          updatedMembers.push(memberToUpdate);
          return updatedMembers;
        });
      },
    );
  }

  onRemoveMember(memberToRemove?: LocalManagementRoleMember) {
    if (!memberToRemove) return;

    this.removingMember.set(true);
    const role = memberToRemove.mgmtRole;

    if (role != undefined) {
      this.api.deleteAdminPermissionRoleMember(
        this.id,
        role,
        memberToRemove.id,
        () => {
          this.removingMember.set(false);
          this.members.update((members) => {
            return members.filter((member) => member !== memberToRemove);
          });
        },
      );
    }
    this.closeDeletePermissionDialog();
  }

  openDeleteDialog(member: LocalManagementRoleMember) {
    this.memberToDelete = member;
    void this.deleteDialog.nativeElement?.show();
  }

  closeDeletePermissionDialog() {
    void this.deleteDialog.nativeElement?.hide();
  }

  identity(member: LocalManagementRoleMember) {
    return member;
  }
}
